// QROS statement-tag editor + read-only chips. QROS-native only — callers
// must gate rendering on isQros(me). Plain inputs + Tailwind (the SPA uses no
// Radix); tokens mirror the Ontology editor's inputClass and the Chips look.

import { useEffect, useState } from 'react';
import type { TermInput } from '@/lib/useEntity';
import { useTagCatalog } from '@/lib/tags';
import type { CatalogTag, TagInput, TagView } from '@/lib/tags';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { EntityPicker } from '@/components/EntityPicker';
import { localName } from '@/lib/useOntology';

const XSD_DECIMAL = 'http://www.w3.org/2001/XMLSchema#decimal';
const XSD_DATE = 'http://www.w3.org/2001/XMLSchema#date';

const tagInputClass =
  'w-full rounded-md border border-border px-2 py-1 text-sm text-tres-text-primary placeholder:text-tres-text-secondary focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action';
const fieldLabel = 'text-xs font-medium text-tres-text-secondary';

type Authority = '' | 'primary' | 'secondary' | 'tertiary';

/** A catalog-picked tag row (tier 1 builtin or tier 2 declared): typed by
 *  `kind` (text | number | date | entity | flag). `required` rows come from
 *  the predicate's `:requiresTag` contract — pre-added, non-removable. */
export interface ExtraTag {
  name: string;
  kind: string;
  value: string;
  /** An entity row's name, beside its IRI in `value`. */
  label?: string;
  required?: boolean;
}

/** Editor state — lifted into the parent form so it serializes at submit. */
export interface TagDraft {
  confidence: string;
  authority: Authority;
  source: string;
  validFrom: string;
  validUntil: string;
  /** The term the fact holds in, by its IRI; `contextLabel` is its name. */
  context: string;
  contextLabel: string;
  /** A name typed into the Context box and not picked from its list: not a
   *  context, and never sent; a form refuses to submit while it is there. */
  contextPending: string;
  within: Array<{ key: string; value: string }>;
  extra: ExtraTag[];
  freeform: Array<{ key: string; value: string }>;
}

export const EMPTY_TAG_DRAFT: TagDraft = {
  confidence: '',
  authority: '',
  source: '',
  validFrom: '',
  validUntil: '',
  context: '',
  contextLabel: '',
  contextPending: '',
  within: [],
  extra: [],
  freeform: [],
};

/** The refusal for a draft whose Context box holds a name not picked from its
 *  list, which would otherwise be dropped without a word (77.9); null when
 *  there is none. */
export function unpickedContext(d: TagDraft): string | null {
  const pending = d.contextPending.trim();
  if (!pending || d.context.trim()) return null;
  return `The context "${pending}" was typed but not picked from the list, so it would not be used. Pick it from the suggestions under the Context box, or clear the box.`;
}

export function isTagDraftEmpty(d: TagDraft): boolean {
  return (
    !d.confidence.trim() &&
    !d.authority &&
    !d.source.trim() &&
    !d.validFrom &&
    !d.validUntil &&
    !d.context.trim() &&
    d.within.every((w) => !w.key.trim() && !w.value.trim()) &&
    d.extra.every((e) => !e.value.trim()) &&
    d.freeform.every((f) => !f.key.trim() && !f.value.trim())
  );
}

function lit(value: string, datatype?: string): TermInput {
  return datatype ? { value, datatype } : { value };
}

/** Pure: turn a draft into the wire `TagInput[]`, dropping blanks and an
 *  out-of-range confidence. `@within.<dim>` dotted keys per dimension. */
export function serializeTags(d: TagDraft): TagInput[] {
  const out: TagInput[] = [];
  const conf = d.confidence.trim();
  if (conf) {
    const n = Number(conf);
    if (!Number.isNaN(n) && n >= 0 && n <= 1) {
      out.push({ name: '@confidence', value: lit(conf, XSD_DECIMAL) });
    }
  }
  if (d.authority) out.push({ name: '@authority', value: lit(d.authority) });
  if (d.source.trim()) out.push({ name: '@source', value: lit(d.source.trim()) });
  if (d.validFrom) out.push({ name: '@validFrom', value: lit(d.validFrom, XSD_DATE) });
  if (d.validUntil) out.push({ name: '@validUntil', value: lit(d.validUntil, XSD_DATE) });
  if (d.context.trim()) out.push({ name: '@context', value: { uri: d.context.trim() } });
  for (const w of d.within) {
    if (w.key.trim() && w.value.trim()) {
      out.push({ name: `@within.${w.key.trim()}`, value: lit(w.value.trim()) });
    }
  }
  for (const e of d.extra) {
    const v = e.value.trim();
    if (!v) continue;
    switch (e.kind) {
      case 'number':
        out.push({ name: e.name, value: lit(v, XSD_DECIMAL) });
        break;
      case 'date':
        out.push({ name: e.name, value: lit(v, XSD_DATE) });
        break;
      case 'entity':
        out.push({ name: e.name, value: { uri: v } });
        break;
      case 'flag':
        if (v === 'true') out.push({ name: e.name, value: lit('true') });
        break;
      default:
        out.push({ name: e.name, value: lit(v) });
    }
  }
  for (const f of d.freeform) {
    const key = f.key.trim();
    if (key && f.value.trim()) {
      out.push({ name: key.startsWith('@') ? key : `@${key}`, value: lit(f.value.trim()) });
    }
  }
  return out;
}

const AUTHORITIES = new Set<Authority>(['primary', 'secondary', 'tertiary']);

/** Engine-stamped provenance tags (`TagValue::Provenance` server-side). The
 *  read wire carries them as display summaries ("2026-07-30 by user"), which
 *  cannot round-trip through a write — resubmitting one would replace the
 *  structured provenance with its summary string. The engine re-stamps them
 *  on every write, so drafts drop them. */
const ENGINE_STAMPED_TAGS = new Set(['@created', '@updated']);

/** Mirror of [`serializeTags`]: seed a draft from a statement's resolved
 *  tags so the per-statement editor opens on the CURRENT tag set. Dedicated
 *  fields fill from their tags; unknown names land as typed `extra` rows
 *  (kind inferred from the value shape) so a resave keeps them. */
export function tagViewsToDraft(tags: TagView[]): TagDraft {
  const d: TagDraft = {
    ...EMPTY_TAG_DRAFT,
    within: [],
    extra: [],
    freeform: [],
  };
  for (const t of tags) {
    if (ENGINE_STAMPED_TAGS.has(t.name)) continue;
    const literal = t.value.kind === 'literal' ? t.value.value : null;
    if (t.name.startsWith('@within.')) {
      d.within.push({
        key: t.name.slice('@within.'.length),
        value: literal ?? (t.value.kind === 'uri' ? t.value.uri : ''),
      });
      continue;
    }
    switch (t.name) {
      case '@confidence':
        if (literal !== null) {
          d.confidence = literal;
          continue;
        }
        break;
      case '@authority':
        if (literal !== null && AUTHORITIES.has(literal as Authority)) {
          d.authority = literal as Authority;
          continue;
        }
        break;
      case '@source':
        if (literal !== null) {
          d.source = literal;
          continue;
        }
        break;
      case '@validFrom':
        if (literal !== null) {
          d.validFrom = literal;
          continue;
        }
        break;
      case '@validUntil':
        if (literal !== null) {
          d.validUntil = literal;
          continue;
        }
        break;
      case '@context':
        d.context = t.value.kind === 'uri' ? t.value.uri : (literal ?? '');
        d.contextLabel = t.value.kind === 'uri' ? t.value.label : '';
        continue;
      default:
        break;
    }
    if (t.value.kind === 'uri') {
      d.extra.push({ name: t.name, kind: 'entity', value: t.value.uri, label: t.value.label });
    } else {
      const kind =
        t.value.datatype === XSD_DECIMAL
          ? 'number'
          : t.value.datatype === XSD_DATE
            ? 'date'
            : 'text';
      d.extra.push({ name: t.name, kind, value: t.value.value });
    }
  }
  return d;
}

function Caret({ open }: { open: boolean }) {
  return (
    <span
      className={`inline-block h-3 w-3 transform text-tres-text-secondary transition-transform ${
        open ? 'rotate-90' : 'rotate-0'
      }`}
      aria-hidden
    >
      ▶
    </span>
  );
}

/** A key→value row list (for @within and free-form). */
function PairRows({
  rows,
  keyPlaceholder,
  onChange,
}: {
  rows: Array<{ key: string; value: string }>;
  keyPlaceholder: string;
  onChange: (rows: Array<{ key: string; value: string }>) => void;
}) {
  const set = (i: number, patch: Partial<{ key: string; value: string }>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            className={tagInputClass}
            placeholder={keyPlaceholder}
            value={r.key}
            onChange={(e) => set(i, { key: e.target.value })}
          />
          <span className="text-tres-text-secondary">=</span>
          <input
            className={tagInputClass}
            placeholder="value"
            value={r.value}
            onChange={(e) => set(i, { value: e.target.value })}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
            aria-label="Remove"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, { key: '', value: '' }])}
        className={buttonVariants({ variant: 'link', size: 'sm' })}
      >
        + add
      </button>
    </div>
  );
}

// Tags with dedicated controls above the dropdown — never offered as options.
const DEDICATED_TAGS = new Set([
  '@confidence',
  '@authority',
  '@source',
  '@validFrom',
  '@validUntil',
  '@context',
  '@within',
]);

/**
 * Controlled QROS tag editor, collapsed behind a `Tags ▸` disclosure so the
 * common authoring path stays clean. Block-level and per-statement override
 * editors share this component (the parent decides how to merge).
 *
 * With a `graph` (and optionally the statement's `predicate`), the editor
 * fetches the three-tier tag catalog: an "Add tag" dropdown offers the
 * builtin catalog + ontology-declared tags (the predicate's allowed tags
 * first), each adding a row typed by the tag's value kind; the predicate's
 * REQUIRED tags are pre-added and non-removable; "Custom…" keeps the
 * free-form escape for user-minted tags.
 */
export function TagEditor({
  value,
  onChange,
  label = 'Tags',
  graph,
  predicate,
  defaultOpen = false,
  collapsible = true,
}: {
  value: TagDraft;
  onChange: (next: TagDraft) => void;
  label?: string;
  graph?: string | undefined;
  predicate?: string | undefined;
  /** The per-statement edit flow opens the fields immediately — the user just
   *  asked to edit tags, so a second disclosure click is noise. */
  defaultOpen?: boolean;
  /** Compose forms show the fields as part of the form rather than behind a
   *  disclosure — a collapsed accordion trailing the bottom of the form reads
   *  as a separate thing rather than part of the statement being built. */
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = collapsible ? open : true;
  const count = serializeTags(value).length;
  const set = <K extends keyof TagDraft>(key: K, v: TagDraft[K]) =>
    onChange({ ...value, [key]: v });

  const catalogQ = useTagCatalog(graph ?? null, predicate ?? null);
  const catalog = catalogQ.data;

  // Pre-add the predicate's required tags as non-removable rows.
  useEffect(() => {
    if (!catalog || catalog.required.length === 0) return;
    const present = new Set(value.extra.map((e) => e.name));
    const byName = new Map(
      [...catalog.declared, ...catalog.builtin].map((t) => [t.name, t] as const),
    );
    const missing = catalog.required.filter((n) => !present.has(n));
    if (missing.length === 0) return;
    onChange({
      ...value,
      extra: [
        ...value.extra,
        ...missing.map((n) => ({
          name: n,
          kind: byName.get(n)?.kind ?? 'text',
          value: '',
          required: true,
        })),
      ],
    });
    // `value`/`onChange` change identity every parent render; keying the
    // effect on them would loop. The required set is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  const optionPool: CatalogTag[] = catalog
    ? (() => {
        const present = new Set(value.extra.map((e) => e.name));
        const allowed = new Set(catalog.allowed);
        const pool = [...catalog.declared, ...catalog.builtin].filter(
          (t) => !DEDICATED_TAGS.has(t.name) && !present.has(t.name),
        );
        // Predicate-allowed declared tags first, then the rest alphabetically.
        pool.sort(
          (a, b) =>
            Number(allowed.has(b.name)) - Number(allowed.has(a.name)) ||
            a.name.localeCompare(b.name),
        );
        return pool;
      })()
    : [];

  const addFromCatalog = (name: string) => {
    if (name === '__custom__') {
      set('freeform', [...value.freeform, { key: '', value: '' }]);
      return;
    }
    const t = optionPool.find((o) => o.name === name);
    if (!t) return;
    set('extra', [...value.extra, { name: t.name, kind: t.kind ?? 'text', value: '' }]);
  };

  return (
    <div className="rounded-md border border-border bg-muted/60 p-2">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'flex w-full items-center gap-1.5')}
        >
          <Caret open={open} />
          {label}
          {count > 0 && (
            <span className="rounded bg-tres-action/15 px-1.5 py-0 text-[10px] text-tres-action">
              {count}
            </span>
          )}
        </button>
      ) : (
        <div className="flex items-center gap-1.5 text-xs font-medium text-tres-text-secondary">
          {label}
          {count > 0 && (
            <span className="rounded bg-tres-action/15 px-1.5 py-0 text-[10px] text-tres-action">
              {count}
            </span>
          )}
        </div>
      )}

      {expanded && (
        <div className="mt-2 space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className={fieldLabel}>Confidence (0–1)</span>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                className={tagInputClass}
                value={value.confidence}
                onChange={(e) => set('confidence', e.target.value)}
              />
            </label>
            <label className="block">
              <span className={fieldLabel}>Authority</span>
              <select
                className={tagInputClass}
                value={value.authority}
                onChange={(e) => set('authority', e.target.value as Authority)}
              >
                <option value="">(none)</option>
                <option value="primary">primary</option>
                <option value="secondary">secondary</option>
                <option value="tertiary">tertiary</option>
              </select>
            </label>
          </div>

          <label className="block">
            <span className={fieldLabel}>Source</span>
            <input
              className={tagInputClass}
              placeholder="where this came from"
              value={value.source}
              onChange={(e) => set('source', e.target.value)}
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className={fieldLabel}>Valid from</span>
              <input
                type="date"
                className={tagInputClass}
                value={value.validFrom}
                onChange={(e) => set('validFrom', e.target.value)}
              />
            </label>
            <label className="block">
              <span className={fieldLabel}>Valid until</span>
              <input
                type="date"
                className={tagInputClass}
                value={value.validUntil}
                onChange={(e) => set('validUntil', e.target.value)}
              />
            </label>
          </div>

          <div className="block">
            <span className={fieldLabel}>Context: the thing this fact holds in</span>
            <EntityPicker
              selected={
                value.context
                  ? { uri: value.context, label: value.contextLabel || localName(value.context) }
                  : null
              }
              onSelect={(sel) =>
                onChange({ ...value, context: sel?.uri ?? '', contextLabel: sel?.label ?? '', contextPending: '' })
              }
              onTextChange={(text) => onChange({ ...value, contextPending: text })}
              placeholder="Search by name, e.g. a game, a season, a case"
              label="Context"
            />
            {value.contextPending.trim() && !value.context && (
              <p className="mt-0.5 text-xs text-tres-error">
                Not used until you pick it from the list.
              </p>
            )}
          </div>

          <div>
            <span className={fieldLabel}>Within (scope dimensions)</span>
            <PairRows
              rows={value.within}
              keyPlaceholder="dimension"
              onChange={(rows) => set('within', rows)}
            />
          </div>

          {value.extra.length > 0 && (
            <div className="space-y-1">
              {value.extra.map((e, i) => (
                <div key={`${e.name}-${i}`} className="flex items-center gap-1.5">
                  <span className="min-w-[110px] font-mono text-xs text-tres-text-secondary">
                    {e.name}
                    {e.required && (
                      <span
                        className="ml-1 text-tres-error"
                        title="Required by this predicate's tag contract"
                      >
                        *
                      </span>
                    )}
                  </span>
                  <ExtraValueInput
                    row={e}
                    onChange={(v, label) =>
                      set(
                        'extra',
                        value.extra.map((x, j) =>
                          j === i ? { ...x, value: v, ...(label !== undefined ? { label } : {}) } : x,
                        ),
                      )
                    }
                  />
                  {!e.required && (
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          'extra',
                          value.extra.filter((_, j) => j !== i),
                        )
                      }
                      className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                      aria-label={`Remove ${e.name}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {(optionPool.length > 0 || graph) && (
            <label className="block">
              <span className={fieldLabel}>Add tag</span>
              <select
                className={tagInputClass}
                value=""
                onChange={(e) => addFromCatalog(e.target.value)}
              >
                <option value="">Pick a tag…</option>
                {optionPool.map((t) => (
                  <option key={t.name} value={t.name} title={t.doc ?? t.label ?? undefined}>
                    {t.name}
                    {t.uri ? ' (declared)' : ''}
                  </option>
                ))}
                <option value="__custom__">Custom…</option>
              </select>
            </label>
          )}

          <div>
            <span className={fieldLabel}>Custom tags</span>
            <PairRows
              rows={value.freeform}
              keyPlaceholder="@key"
              onChange={(rows) => set('freeform', rows)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Value control for a catalog-picked tag row, typed by its value kind. */
function ExtraValueInput({ row, onChange }: { row: ExtraTag; onChange: (v: string, label?: string) => void }) {
  switch (row.kind) {
    case 'number':
      return (
        <input
          type="number"
          className={tagInputClass}
          value={row.value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          className={tagInputClass}
          value={row.value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'flag':
      return (
        <input
          type="checkbox"
          checked={row.value === 'true'}
          onChange={(e) => onChange(e.target.checked ? 'true' : '')}
        />
      );
    case 'entity':
      return (
        <div className="min-w-0 flex-1">
          <EntityPicker
            selected={row.value ? { uri: row.value, label: row.label || localName(row.value) } : null}
            onSelect={(sel) => onChange(sel?.uri ?? '', sel?.label ?? '')}
            placeholder="Search by name"
            label={row.name}
          />
        </div>
      );
    default:
      return (
        <input
          className={tagInputClass}
          placeholder="value"
          value={row.value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

// Auto-applied provenance tags — present on virtually every statement, so they're
// noise relative to semantic tags (minCardinality, confidence, …). Rendered muted
// and after the semantic ones so the meaningful tags stand out.
const PROVENANCE_TAGS = new Set([
  '@created',
  '@updated',
  '@source',
  '@importedAt',
  '@convertedFrom',
]);

/** Read-only chips for a statement's resolved tags. Display only. */
export function TagChips({ tags }: { tags: TagView[] | undefined }) {
  if (!tags || tags.length === 0) return null;
  const semantic = tags.filter((t) => !PROVENANCE_TAGS.has(t.name));
  const provenance = tags.filter((t) => PROVENANCE_TAGS.has(t.name));
  const chip = (t: TagView, i: number, muted: boolean) => (
    <li
      key={`${t.name}-${i}`}
      className={
        muted
          ? 'inline-flex items-center gap-1 rounded border border-transparent px-1.5 py-0.5 text-[10px] text-tres-text-secondary/60'
          : 'inline-flex items-center gap-1 rounded border border-tres-action/30 bg-tres-submenu-bg px-1.5 py-0.5 text-[10px] text-tres-text-secondary'
      }
      title={t.name}
    >
      <span className={muted ? 'font-medium' : 'font-medium text-tres-text-primary'}>
        {t.name.replace(/^@/, '')}
      </span>
      {t.value.kind === 'uri' ? (
        <span title={t.value.uri}>{t.value.label || t.value.uri}</span>
      ) : (
        <span>{t.value.value}</span>
      )}
    </li>
  );
  return (
    <ul className="mt-0.5 flex flex-wrap gap-1">
      {semantic.map((t, i) => chip(t, i, false))}
      {provenance.map((t, i) => chip(t, i, true))}
    </ul>
  );
}

const MAX_NAME_CHIPS = 4;

/** Quiet tag-NAME chips for search result rows: which semantic tags exist on
 *  the entity's statements, without values. Auto-applied provenance names are
 *  noise on virtually every row and are dropped; truncates with "+N". */
export function TagNameChips({ names }: { names: string[] | undefined }) {
  if (!names || names.length === 0) return null;
  const semantic = names.filter((n) => !PROVENANCE_TAGS.has(n));
  if (semantic.length === 0) return null;
  const shown = semantic.slice(0, MAX_NAME_CHIPS);
  const more = semantic.length - shown.length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((n) => (
        <span
          key={n}
          title={n}
          className="rounded border border-transparent bg-muted px-1.5 py-0.5 text-[10px] text-tres-text-secondary/80"
        >
          {n.replace(/^@/, '')}
        </span>
      ))}
      {more > 0 && (
        <span className="text-[10px] text-tres-text-secondary/60">+{more}</span>
      )}
    </span>
  );
}
